import React, { forwardRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import Tooltip from '../Popups/Tooltip'
import useSelectBase from './Select.hook'
import {
  BaseInput,
  BaseSelect,
  BottomMessage,
  Content,
  DropDown,
  DropDownIcon,
  Error,
  Info,
  Label,
  Option,
  OptionContent,
  Placeholder,
  Required,
  Root,
  Skeleton,
  TooltipIcon
} from './Select.styles'

export const useSelect = useSelectBase

/**
 * The Select' component.
 */
const Select = forwardRef((props, ref) => {
  const {
    disabled,
    error,
    info,
    label,
    loading,
    margin,
    maxWidth,
    options,
    padding,
    placeholder,
    required,
    size,
    success,
    textAlign,
    tooltip,
    value,
    onChange,
    ...restProps
  } = props
  const handleBlur = useCallback(
    event => {
      if (restProps?.onBlur && typeof restProps?.onBlur === 'function') {
        restProps?.onBlur(event)
      }

      onChange(event)
    },
    [onChange, restProps]
  )
  const handleClick = useCallback(event => event.stopPropagation(), [])
  const renderIcon = useCallback(
    () => (
      <DropDown>
        <DropDownIcon size={size} />
      </DropDown>
    ),
    [size]
  )
  const showPlaceholder = useMemo(() => placeholder !== '' && !value && value !== 0, [placeholder, value])

  return (
    <Root margin={margin} padding={padding} ref={ref}>
      {label && (
        <Label size={size}>
          {label}
          {required && <Required size={size} />}
          {tooltip && (
            <Tooltip content={tooltip}>
              <TooltipIcon focusable tanIndex="0" size={size} />
            </Tooltip>
          )}
        </Label>
      )}
      <Content>
        {loading && <Skeleton size={size} variant="rectangular" />}
        {!loading && (
          <BaseSelect
            {...restProps}
            disabled={disabled}
            error={Boolean(error)}
            IconComponent={renderIcon}
            input={<BaseInput />}
            maxWidth={maxWidth}
            MenuProps={{
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
              anchorReference: 'anchorEl'
            }}
            size={size}
            success={success}
            textAlign={textAlign}
            value={value}
            onBlur={handleBlur}
            onChange={onChange}
            onClick={handleClick}
          >
            {options.map(option => (
              <Option disableGutters key={option.value} value={option.value}>
                <OptionContent title={option.name}>{option.name}</OptionContent>
              </Option>
            ))}
          </BaseSelect>
        )}
        {!loading && showPlaceholder && (
          <Placeholder size={size} textAlign={textAlign}>
            {placeholder}
          </Placeholder>
        )}
      </Content>
      <BottomMessage size={size}>
        {!error && <Info>{info}</Info>}
        {error && !loading && <Error>{error}</Error>}
      </BottomMessage>
    </Root>
  )
})

Select.defaultProps = {
  disabled: false,
  error: '',
  info: '',
  label: '',
  loading: false,
  margin: 0,
  options: [],
  padding: 0,
  placeholder: 'Seleccione...',
  required: false,
  size: 'medium',
  success: false,
  textAlign: 'initial',
  tooltip: '',
  type: 'text'
}

Select.propTypes = {
  /**
   * Disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Error.
   */
  error: PropTypes.string,
  /**
   * Info.
   */
  info: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Label.
   */
  label: PropTypes.string,
  /**
   * Loading.
   */
  loading: PropTypes.bool,
  /**
   * Margin.
   */
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Max width.
   */
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Options.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ),
  /**
   * Padding.
   */
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Placeholder.
   */
  placeholder: PropTypes.string,
  /**
   * Required.
   */
  required: PropTypes.bool,
  /**
   * Size.
   */
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  /**
   * Success.
   */
  success: PropTypes.bool,
  /**
   * Text align.
   */
  textAlign: PropTypes.oneOf(['center', 'end', 'initial', 'start']),
  /**
   * Tooltip.
   */
  tooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Value.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /**
   * On change.
   */
  onChange: PropTypes.func.isRequired
}

export default Select
